import {
	findNearestFreeformDuplicationSpot,
	getDuplicatedWidgetSize,
} from '../layout/webDashboardDuplication';
import {
	applyResizedWidgetRect,
	getResizeGridMetrics,
	getSnappedWaterNetworkResizeSize,
} from '../layout/webDashboardResizeGeometry';
import { isImmediateDropAllowedWidget } from '../layout/webDashboardDropGeometry';

export const createDuplicateWidgetHandler = ({
	dashboard,
	setDashboard,
	getEditableMetrics,
	getWidgetRectPx,
	getMinimumWidgetSizeForType,
	defaultDivWidth,
	defaultDivHeight,
	isWidgetFreeMovementEnabled,
	toPxNumber,
	checkOverlap,
	findNearestEmptySpot,
	cloneDashboardValue,
	storeWidgetPosition,
	sortWidgets,
	setAlertMessage,
	t,
	setAlertType,
	setShowAlert,
}) => (id) => {
	const elem = (dashboard.widgets || []).find(e => e.id === id);
	if (!elem) return;

	const metrics = getEditableMetrics();
	const gap = metrics?.gap || 0;
	const elemRectPx = getWidgetRectPx(elem, metrics);
	const minimumWidgetSize = getMinimumWidgetSizeForType(
		elem?.type,
		defaultDivWidth,
		defaultDivHeight,
	);
	const isFreeformLayoutWidget = isWidgetFreeMovementEnabled(elem);
	const {
		widgetWidth,
		widgetHeight,
	} = getDuplicatedWidgetSize({
		rawWidth: elemRectPx.width,
		rawHeight: elemRectPx.height,
		minimumWidgetSize,
		isFreeformLayoutWidget,
		defaultDivWidth,
		defaultDivHeight,
		gap,
	});

	const startLeft = elemRectPx.left;
	const startTop = elemRectPx.top;
	const belowOffset = isFreeformLayoutWidget
		? widgetHeight
		: widgetHeight + gap;
	const preferredDuplicateSpots = [
		{ left: startLeft, top: startTop + belowOffset },
	];
	const extraSearchHeight = Math.max(defaultDivHeight + gap, widgetHeight + gap);

	let duplicationSpot = null;
	if (isFreeformLayoutWidget) {
		duplicationSpot = findNearestFreeformDuplicationSpot({
			startLeft,
			startTop,
			widgetWidth,
			widgetHeight,
			widgetsToCheck: dashboard?.widgets || [],
			metrics,
			defaultDivWidth,
			defaultDivHeight,
			toPxNumber,
			checkOverlap,
			subjectWidget: elem,
			preferredSpots: preferredDuplicateSpots,
			extraSearchHeight,
		});
	}

	const { found, left: newLeft, top: newTop } = duplicationSpot || findNearestEmptySpot(
		startLeft,
		startTop,
		widgetWidth,
		widgetHeight,
		preferredDuplicateSpots,
		extraSearchHeight,
	);

	if (found) {
		const duplicatedSource = cloneDashboardValue(elem);
		const duplicatedWidget = storeWidgetPosition(
			{
				...duplicatedSource,
				id: elem.type + Date.now(),
			},
			{
				left: newLeft,
				top: newTop,
				width: widgetWidth,
				height: widgetHeight,
			},
			metrics,
		);
		const updatedWidgets = [
			...(dashboard.widgets || []),
			duplicatedWidget,
		];

		setDashboard({
			...dashboard,
			widgets: sortWidgets(updatedWidgets),
		});
	} else {
		setAlertMessage(t.noSpaceForDuplication || 'No empty space available on the dashboard for duplication.');
		setAlertType('warning');
		setShowAlert(true);
	}
};

export const createResizeWidgetHandler = ({
	dashboard,
	setDashboard,
	defaultDivWidth,
	defaultDivHeight,
	getMinimumWidgetSizeForType,
	isPercentageLayout,
	getWidgetRectPx,
	getEditableMetrics,
	isWidgetFreeMovementEnabled,
	checkIsOnBackgroundImage,
	getContainingBackgroundImage,
	getOverlappingBackgroundImage,
	canWaterWidgetsShareSpace,
	isWaterNetworkWidgetType,
	snapWaterNetworkResizeRect,
	checkOverlap,
	setAlertMessage,
	t,
	setAlertType,
	setShowAlert,
	serializeRectForStorage,
}) => (e, id, setDims) => {
	e.preventDefault();
	e.stopPropagation();

	const resizeHandle = e.currentTarget;
	if (resizeHandle?.setPointerCapture && e.pointerId !== undefined) {
		try {
			resizeHandle.setPointerCapture(e.pointerId);
		} catch (_error) {
			// Pointer capture can fail if the pointer was already released.
		}
	}

	const widget = e.target.closest('.widget'),
		style = document.defaultView.getComputedStyle(widget),
		startWidth = parseFloat(style.width), startHeight = parseFloat(style.height),
		startClientX = e.clientX,
		startClientY = e.clientY,
		dashboardWidgets = dashboard?.widgets || [],
		widgetBeingResized = dashboardWidgets.find(wd => wd.id === id),
		minimumWidgetSize = getMinimumWidgetSizeForType(
			widgetBeingResized?.type,
			defaultDivWidth,
			defaultDivHeight,
		),
		moveListener = (event) => {
			event.preventDefault();
			const newWidth = Math.max(minimumWidgetSize.width, startWidth + event.clientX - startClientX),
				newHeight = Math.max(minimumWidgetSize.height, startHeight + event.clientY - startClientY);
			if (isPercentageLayout) {
				widget.style.width = `${newWidth}px`;
				widget.style.height = `${newHeight}px`;
			}
			setDims({ width: newWidth, height: newHeight });
		};

	document.documentElement.addEventListener('pointermove', moveListener);

	document.documentElement.addEventListener('pointerup', function upListener(ev) {
		const editableElement = document.getElementById('editable');
		if (!editableElement) {
			document.documentElement.removeEventListener('pointermove', moveListener);
			document.documentElement.removeEventListener('pointerup', upListener);
			return;
		}

		const editableRect = editableElement.getBoundingClientRect();
		const widgetRect = widget.getBoundingClientRect();

		const currentWidgetRelativeLeft = widgetRect.left - editableRect.left;
		const currentWidgetRelativeTop = widgetRect.top - editableRect.top;
		const widgetBeingResizedRect = getWidgetRectPx(widgetBeingResized, getEditableMetrics());
		const isResizingBackgroundImage = widgetBeingResized && isImmediateDropAllowedWidget(widgetBeingResized.type);
		const isFreeformResize = isWidgetFreeMovementEnabled(widgetBeingResized);

		const divertedElement = document.getElementById('diverted');
		const {
			gap,
			gridColumnSize,
			gridRowSize,
		} = getResizeGridMetrics({
			divertedElement,
			defaultDivWidth,
			defaultDivHeight,
		});

		const mouseRelativeX = ev.clientX - editableRect.left;
		const mouseRelativeY = ev.clientY - editableRect.top;

		let rawPotentialWidth = mouseRelativeX - currentWidgetRelativeLeft;
		let rawPotentialHeight = mouseRelativeY - currentWidgetRelativeTop;

		rawPotentialWidth = Math.max(minimumWidgetSize.width, rawPotentialWidth);
		rawPotentialHeight = Math.max(minimumWidgetSize.height, rawPotentialHeight);

		const isOnBackgroundImage = !isResizingBackgroundImage && checkIsOnBackgroundImage(
			currentWidgetRelativeLeft,
			currentWidgetRelativeTop,
			rawPotentialWidth,
			rawPotentialHeight,
			dashboardWidgets,
			id
		);
		const containingBackground = isOnBackgroundImage
			? (
				getContainingBackgroundImage(
					currentWidgetRelativeLeft,
					currentWidgetRelativeTop,
					dashboardWidgets,
					id
				) || getOverlappingBackgroundImage(
					currentWidgetRelativeLeft,
					currentWidgetRelativeTop,
					rawPotentialWidth,
					rawPotentialHeight,
					dashboardWidgets,
					id,
				))
			: null;

		if (containingBackground) {
			const containingBackgroundRect = getWidgetRectPx(containingBackground, getEditableMetrics());
			const backgroundLeft = containingBackgroundRect.left;
			const backgroundTop = containingBackgroundRect.top;
			const backgroundRight = backgroundLeft + containingBackgroundRect.width;
			const backgroundBottom = backgroundTop + containingBackgroundRect.height;

			const maxWidthInsideBackground = Math.max(minimumWidgetSize.width, backgroundRight - currentWidgetRelativeLeft);
			const maxHeightInsideBackground = Math.max(minimumWidgetSize.height, backgroundBottom - currentWidgetRelativeTop);

			const finalFreeWidth = Math.max(
				minimumWidgetSize.width,
				Math.min(rawPotentialWidth, maxWidthInsideBackground)
			);
			const finalFreeHeight = Math.max(
				minimumWidgetSize.height,
				Math.min(rawPotentialHeight, maxHeightInsideBackground)
			);

			let finalWidthOnBackground = finalFreeWidth;
			let finalHeightOnBackground = finalFreeHeight;
			let maxRightEdge = currentWidgetRelativeLeft + finalWidthOnBackground;
			let maxBottomEdge = currentWidgetRelativeTop + finalHeightOnBackground;

			dashboardWidgets.forEach(otherWidget => {
				if (otherWidget.id === id) return;
				if (otherWidget.type === 'background-image' || otherWidget.type === 'box-widget') return;
				if (canWaterWidgetsShareSpace(widgetBeingResized, otherWidget)) return;

				const otherRectPx = getWidgetRectPx(otherWidget, getEditableMetrics());
				const otherLeft = otherRectPx.left;
				const otherTop = otherRectPx.top;
				const otherWidth = otherRectPx.width;
				const otherHeight = otherRectPx.height;
				const otherRight = otherLeft + otherWidth;
				const otherBottom = otherTop + otherHeight;

				const hasVerticalOverlap = (
					currentWidgetRelativeTop < otherBottom &&
					currentWidgetRelativeTop + finalHeightOnBackground > otherTop
				);
				if (hasVerticalOverlap && otherLeft > currentWidgetRelativeLeft) {
					maxRightEdge = Math.min(maxRightEdge, otherLeft);
				}

				const hasHorizontalOverlap = (
					currentWidgetRelativeLeft < otherRight &&
					currentWidgetRelativeLeft + finalWidthOnBackground > otherLeft
				);
				if (hasHorizontalOverlap && otherTop > currentWidgetRelativeTop) {
					maxBottomEdge = Math.min(maxBottomEdge, otherTop);
				}
			});

			finalWidthOnBackground = Math.max(minimumWidgetSize.width, maxRightEdge - currentWidgetRelativeLeft);
			finalHeightOnBackground = Math.max(minimumWidgetSize.height, maxBottomEdge - currentWidgetRelativeTop);
			finalWidthOnBackground = Math.min(finalWidthOnBackground, finalFreeWidth);
			finalHeightOnBackground = Math.min(finalHeightOnBackground, finalFreeHeight);

			if (isFreeformResize && isWaterNetworkWidgetType(widgetBeingResized)) {
				const snappedResizeSize = getSnappedWaterNetworkResizeSize({
					left: currentWidgetRelativeLeft,
					top: currentWidgetRelativeTop,
					width: finalWidthOnBackground,
					height: finalHeightOnBackground,
					widgetBeingResized,
					dashboardWidgets,
					getWidgetRectPx,
					metrics: getEditableMetrics(),
					id,
					snapWaterNetworkResizeRect,
				});
				finalWidthOnBackground = snappedResizeSize.width;
				finalHeightOnBackground = snappedResizeSize.height;
			}

			const stillOverlaps = checkOverlap(
				currentWidgetRelativeLeft,
				currentWidgetRelativeTop,
				finalWidthOnBackground,
				finalHeightOnBackground,
				dashboardWidgets,
				id
				,
				widgetBeingResized,
			);

			if (stillOverlaps) {
				const previousWidth = widgetBeingResizedRect.width || minimumWidgetSize.width;
				const previousHeight = widgetBeingResizedRect.height || minimumWidgetSize.height;
				if (isPercentageLayout) {
					widget.style.width = `${previousWidth}px`;
					widget.style.height = `${previousHeight}px`;
				}
				setDims({ width: previousWidth, height: previousHeight });
				setAlertMessage(
					t.noOverlapOnBackgroundImage ||
					'Widgets cannot overlap, even on top of a background image.'
				);
				setAlertType('warning');
				setShowAlert(true);
				document.documentElement.removeEventListener('pointermove', moveListener);
				document.documentElement.removeEventListener('pointerup', upListener);
				return;
			}

			setDims({ width: finalWidthOnBackground, height: finalHeightOnBackground });
			setDashboard({
				...dashboard,
				widgets: applyResizedWidgetRect({
					widgets: dashboardWidgets,
					id,
					rect: {
						left: currentWidgetRelativeLeft,
						top: currentWidgetRelativeTop,
						width: finalWidthOnBackground,
						height: finalHeightOnBackground,
					},
					serializeRectForStorage,
				})
			});
			document.documentElement.removeEventListener('pointermove', moveListener);
			document.documentElement.removeEventListener('pointerup', upListener);
			return;
		}

		let potentialWidth = isFreeformResize
			? mouseRelativeX - currentWidgetRelativeLeft
			: (Math.round(mouseRelativeX / gridColumnSize) * gridColumnSize) - currentWidgetRelativeLeft;
		let potentialHeight = isFreeformResize
			? mouseRelativeY - currentWidgetRelativeTop
			: (Math.round(mouseRelativeY / gridRowSize) * gridRowSize) - currentWidgetRelativeTop;

		potentialWidth = Math.max(minimumWidgetSize.width, potentialWidth);
		potentialHeight = Math.max(minimumWidgetSize.height, potentialHeight);

		let actualMaxRightEdge = editableRect.width;
		let actualMaxBottomEdge = editableRect.height;

		if (!isResizingBackgroundImage) {
			dashboardWidgets.forEach(otherWidget => {
				if (otherWidget.id === id) {
					return;
				}
				if (canWaterWidgetsShareSpace(widgetBeingResized, otherWidget)) {
					return;
				}

				const otherRectPx = getWidgetRectPx(otherWidget, getEditableMetrics());
				const otherLeft = otherRectPx.left;
				const otherTop = otherRectPx.top;
				const otherWidth = otherRectPx.width;
				const otherHeight = otherRectPx.height;
				const otherRight = otherLeft + otherWidth;
				const otherBottom = otherTop + otherHeight;

				const verticalOverlapWithPotential = (currentWidgetRelativeTop < otherBottom && currentWidgetRelativeTop + potentialHeight > otherTop);
				if (verticalOverlapWithPotential && otherLeft > currentWidgetRelativeLeft) {
					actualMaxRightEdge = Math.min(actualMaxRightEdge, otherLeft);
				}

				const horizontalOverlapWithPotential = (currentWidgetRelativeLeft < otherRight && currentWidgetRelativeLeft + potentialWidth > otherLeft);
				if (horizontalOverlapWithPotential && otherTop > currentWidgetRelativeTop) {
					actualMaxBottomEdge = Math.min(actualMaxBottomEdge, otherTop);
				}
			});
		}

		let finalAdjustedWidth = actualMaxRightEdge - currentWidgetRelativeLeft;
		let finalAdjustedHeight = actualMaxBottomEdge - currentWidgetRelativeTop;

		finalAdjustedWidth = Math.min(finalAdjustedWidth, potentialWidth);
		finalAdjustedHeight = Math.min(finalAdjustedHeight, potentialHeight);

		finalAdjustedWidth = Math.max(minimumWidgetSize.width, finalAdjustedWidth);
		finalAdjustedHeight = Math.max(minimumWidgetSize.height, finalAdjustedHeight);

		let finalSnappingWidth = finalAdjustedWidth;
		let finalSnappingHeight = finalAdjustedHeight;

		if (!isFreeformResize && gridColumnSize > 0) {
			const mouseRightRelative = ev.clientX - editableRect.left;
			let numCols = Math.ceil((mouseRightRelative - currentWidgetRelativeLeft) / gridColumnSize);
			numCols = Math.max(1, numCols);

			finalSnappingWidth = (numCols * defaultDivWidth) + (Math.max(0, numCols - 1) * gap);

			finalSnappingWidth = Math.min(finalSnappingWidth, actualMaxRightEdge - currentWidgetRelativeLeft);
			finalSnappingWidth = Math.max(minimumWidgetSize.width, finalSnappingWidth);
		}

		if (!isFreeformResize && gridRowSize > 0) {
			const mouseBottomRelative = ev.clientY - editableRect.top;
			let numRows = Math.ceil((mouseBottomRelative - currentWidgetRelativeTop) / gridRowSize);
			numRows = Math.max(1, numRows);

			finalSnappingHeight = (numRows * defaultDivHeight) + (Math.max(0, numRows - 1) * gap);

			finalSnappingHeight = Math.min(finalSnappingHeight, actualMaxBottomEdge - currentWidgetRelativeTop);
			finalSnappingHeight = Math.max(minimumWidgetSize.height, finalSnappingHeight);
		}

		finalAdjustedWidth = finalSnappingWidth;
		finalAdjustedHeight = finalSnappingHeight;

		if (isFreeformResize && isWaterNetworkWidgetType(widgetBeingResized)) {
			const snappedResizeSize = getSnappedWaterNetworkResizeSize({
				left: currentWidgetRelativeLeft,
				top: currentWidgetRelativeTop,
				width: finalAdjustedWidth,
				height: finalAdjustedHeight,
				widgetBeingResized,
				dashboardWidgets,
				getWidgetRectPx,
				metrics: getEditableMetrics(),
				id,
				snapWaterNetworkResizeRect,
			});
			finalAdjustedWidth = snappedResizeSize.width;
			finalAdjustedHeight = snappedResizeSize.height;
		}

		setDims({ width: finalAdjustedWidth, height: finalAdjustedHeight });
		setDashboard({
			...dashboard,
			widgets: applyResizedWidgetRect({
				widgets: dashboardWidgets,
				id,
				rect: {
					left: currentWidgetRelativeLeft,
					top: currentWidgetRelativeTop,
					width: finalAdjustedWidth,
					height: finalAdjustedHeight,
				},
				serializeRectForStorage,
			})
		});
		document.documentElement.removeEventListener('pointermove', moveListener);
		document.documentElement.removeEventListener('pointerup', upListener);
	});
};
